import {useCallback, useMemo, useEffect} from 'react';
import {KioskoContext} from './KioskoContext';
import {useKioskoConnection} from './hooks/useKioskoConnection';
import {useKioskoStore} from './useKioskoStore';
import type {
  KioskoProviderProps,
  KioskoContextValue,
  KioskoWebSocketResponse,
} from './types';

export const KioskoProvider = ({
  children,
  autoConnect = true,
  apiKey,
}: KioskoProviderProps) => {
  const {
    setScannerState,
    resetScannerState,
    setConnectionState,
    setApiKey: setStoreApiKey,
    setPrinterState,
  } = useKioskoStore();

  useEffect(() => {
    if (apiKey) {
      setStoreApiKey(apiKey);
    }
  }, [apiKey, setStoreApiKey]);

  const {disconnect, sendMessage, subscribeToEvents} = useKioskoConnection({
    autoConnect,
    apiKey,
    onConnectionStateChange: setConnectionState,
  });

  const handleScannerInitialized = useCallback(() => {
    setScannerState({isInitialized: true});
  }, [setScannerState]);

  const handleDocumentScanned = useCallback(
    (data: KioskoWebSocketResponse) => {
      setScannerState({
        lastScanResult: data.scan_data ?? null,
        isScanning: false,
        error: data.error ?? '',
        success: data.success,
      });
    },
    [setScannerState],
  );

  const handlePrintedCardSuccess = useCallback(() => {
    setPrinterState({
      isPrintingSuccess: true,
      isPrintingError: false,
    });
  }, [setPrinterState]);

  const handlePrintedCardError = useCallback(() => {
    setPrinterState({
      isPrintingError: true,
      isPrintingSuccess: false,
    });
  }, [setPrinterState]);

  const handleResetScannerState = useCallback(() => {
    resetScannerState();
  }, [resetScannerState]);

  useEffect(() => {
    const unsubscribe = subscribeToEvents({
      onScannerInitialized: handleScannerInitialized,
      onDocumentScanned: handleDocumentScanned,
      onPrintedCard: handlePrintedCardSuccess,
      onPrintedCardError: handlePrintedCardError,
    });

    return unsubscribe;
  }, [
    subscribeToEvents,
    handleScannerInitialized,
    handleDocumentScanned,
    handlePrintedCardSuccess,
    handlePrintedCardError,
  ]);

  const contextValue: KioskoContextValue = useMemo(
    () => ({
      disconnect,
      sendMessage,
      subscribeToEvents,
      handleResetScannerState,
    }),
    [disconnect, sendMessage, subscribeToEvents, handleResetScannerState],
  );

  return <KioskoContext.Provider value={contextValue}>{children}</KioskoContext.Provider>;
};
